import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type SuggestionStatus = 'em_analise' | 'aprovada' | 'planejada' | 'em_desenvolvimento' | 'concluida' | 'recusada';
export type SuggestionCategory = 'interface' | 'funcionalidade' | 'desempenho' | 'integracao' | 'relatorio' | 'geral';

export interface Suggestion {
  id: string;
  user_id: string;
  title: string;
  description: string;
  category: SuggestionCategory;
  status: SuggestionStatus;
  moderation_notes: string | null;
  votes_count: number;
  downvotes_count: number;
  created_at: string;
  updated_at: string;
  is_own?: boolean;
  user_has_voted?: boolean;
  user_has_downvoted?: boolean;
}

export const SUGGESTION_CATEGORIES: { value: SuggestionCategory; label: string; icon: string }[] = [
  { value: 'funcionalidade', label: 'Nova funcionalidade', icon: 'ri-add-circle-line' },
  { value: 'interface',      label: 'Interface / UX',      icon: 'ri-layout-line' },
  { value: 'desempenho',     label: 'Desempenho',          icon: 'ri-speed-line' },
  { value: 'integracao',     label: 'Integração',          icon: 'ri-links-line' },
  { value: 'relatorio',      label: 'Relatório / Análise', icon: 'ri-bar-chart-line' },
  { value: 'geral',          label: 'Geral',               icon: 'ri-chat-1-line' },
];

export const SUGGESTION_STATUS_META: Record<SuggestionStatus, { label: string; cls: string; icon: string }> = {
  em_analise:       { label: 'Em análise',      cls: 'bg-amber-100 text-amber-700',     icon: 'ri-search-line' },
  aprovada:         { label: 'Aprovada',        cls: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' },
  planejada:        { label: 'Planejada',       cls: 'bg-sky-100 text-sky-700',         icon: 'ri-calendar-check-line' },
  em_desenvolvimento:{ label: 'Desenvolvendo', cls: 'bg-violet-100 text-violet-700',   icon: 'ri-code-line' },
  concluida:        { label: 'Concluída',       cls: 'bg-teal-100 text-teal-700',       icon: 'ri-check-double-line' },
  recusada:         { label: 'Não aprovada',   cls: 'bg-gray-100 text-gray-500',       icon: 'ri-close-circle-line' },
};

const PUBLIC_STATUSES: SuggestionStatus[] = ['em_analise', 'aprovada', 'planejada', 'em_desenvolvimento', 'concluida'];

export const useSuggestions = () => {
  const { user } = useAuth();
  const [publicSuggestions, setPublicSuggestions] = useState<Suggestion[]>([]);
  const [mySuggestions, setMySuggestions] = useState<Suggestion[]>([]);
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set());
  const [myDownvotes, setMyDownvotes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevUserIdRef.current !== user?.id) {
      if (prevUserIdRef.current !== undefined || !user?.id) {
        setPublicSuggestions([]); setMySuggestions([]); setMyVotes(new Set()); setMyDownvotes(new Set());
      }
      prevUserIdRef.current = user?.id;
    }
  }, [user?.id]);

  const fetchAll = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const [{ data: allData }, { data: myVotesData }, { data: myDownvotesData }] = await Promise.all([
      supabase
        .from('suggestions')
        .select('*')
        .order('votes_count', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('suggestion_votes')
        .select('suggestion_id')
        .eq('user_id', user.id),
      supabase
        .from('suggestion_downvotes')
        .select('suggestion_id')
        .eq('user_id', user.id),
    ]);

    const votedSet = new Set<string>(
      (myVotesData ?? []).map((v: any) => v.suggestion_id as string)
    );
    const downvotedSet = new Set<string>(
      (myDownvotesData ?? []).map((v: any) => v.suggestion_id as string)
    );
    setMyVotes(votedSet);
    setMyDownvotes(downvotedSet);

    const all = ((allData as Suggestion[]) ?? []).map(s => ({
      ...s,
      downvotes_count: s.downvotes_count ?? 0,
      is_own: s.user_id === user.id,
      user_has_voted: votedSet.has(s.id),
      user_has_downvoted: downvotedSet.has(s.id),
    }));

    // Aba pública mostra status publicáveis; recusadas ficam em aba separada
    setPublicSuggestions(all.filter(s => PUBLIC_STATUSES.includes(s.status)));
    setMySuggestions(all.filter(s => s.user_id === user.id));
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createSuggestion = async (values: {
    title: string;
    description: string;
    category: SuggestionCategory;
  }): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Não autenticado.' };
    const { error } = await supabase.from('suggestions').insert({
      user_id: user.id, ...values, status: 'em_analise', votes_count: 0, downvotes_count: 0,
    });
    if (error) return { success: false, error: error.message };
    await fetchAll();
    return { success: true };
  };

  const vote = async (suggestionId: string): Promise<void> => {
    if (!user) return;
    const hasVoted = myVotes.has(suggestionId);
    if (hasVoted) {
      await supabase.from('suggestion_votes').delete()
        .eq('user_id', user.id).eq('suggestion_id', suggestionId);
    } else {
      await supabase.from('suggestion_votes').insert({ user_id: user.id, suggestion_id: suggestionId });
    }
    await fetchAll();
  };

  const downvote = async (suggestionId: string): Promise<void> => {
    if (!user) return;
    const hasDownvoted = myDownvotes.has(suggestionId);
    if (hasDownvoted) {
      await supabase.from('suggestion_downvotes').delete()
        .eq('user_id', user.id).eq('suggestion_id', suggestionId);
      await supabase.from('suggestions').update({
        downvotes_count: Math.max(0, (publicSuggestions.find(s => s.id === suggestionId)?.downvotes_count ?? 1) - 1),
      }).eq('id', suggestionId);
    } else {
      await supabase.from('suggestion_downvotes').insert({ user_id: user.id, suggestion_id: suggestionId });
      await supabase.from('suggestions').update({
        downvotes_count: (publicSuggestions.find(s => s.id === suggestionId)?.downvotes_count ?? 0) + 1,
      }).eq('id', suggestionId);
    }
    await fetchAll();
  };

  return { publicSuggestions, mySuggestions, loading, createSuggestion, vote, downvote, myVotes, myDownvotes, refetch: fetchAll };
};

// ── Admin hook ──────────────────────────────────────────────────────────────
export const useAdminSuggestions = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('suggestions')
      .select('*')
      .order('votes_count', { ascending: false })
      .order('created_at', { ascending: false });
    setSuggestions((data as Suggestion[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateSuggestion = async (id: string, status: SuggestionStatus, notes?: string) => {
    const { error } = await supabase
      .from('suggestions')
      .update({ status, moderation_notes: notes ?? null, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await fetchAll();
    return { error: error?.message ?? null };
  };

  const deleteSuggestion = async (id: string): Promise<{ error: string | null }> => {
    // Otimisticamente remove do estado local para UX instantânea
    setSuggestions(prev => prev.filter(s => s.id !== id));
    // Remove votes first (FK constraint safety)
    await supabase.from('suggestion_downvotes').delete().eq('suggestion_id', id);
    await supabase.from('suggestion_votes').delete().eq('suggestion_id', id);
    const { error } = await supabase.from('suggestions').delete().eq('id', id);
    if (error) {
      // Falhou: restaurar estado real do banco
      await fetchAll();
      return { error: error.message };
    }
    return { error: null };
  };

  return { suggestions, loading, updateSuggestion, deleteSuggestion, refetch: fetchAll };
};
